import { useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import Table from '~/shared/components/Table';
import { useXFetch } from '~/shared/utils/useXFetch';
import {
  OptLogVolumeActionsCountResponse,
  OptLogVolumeActionsResponse,
  OptLogVolumeActions,
} from '~/shared/types/api';
import { TABLE_LIVE_REFRESH_MS } from '~/shared/constants/polling';
import { generateSkeletonRows, createSkeletonAction } from '~/shared/utils/skeletonUtils';

import { getOperationColumnDefs } from '../../ReportTable/columnDefs';

import OptimizationActions from './OptimizationActions';

interface OptimizationTableQueryResult {
  actions: (OptLogVolumeActions & { _isSkeleton?: boolean })[];
  count: number;
}

interface OptimizationTableProps {
  tableQueryUrl: string;
  countQueryUrl: string;
  onPageChange: (page: number) => void;
  onSortChange: (sort: { field: string; dir: 'desc' | 'asc' }) => void;
  dataLimit: number;
  currentSortField: { field: string; dir: 'desc' | 'asc' };
  currentOffset: string;
  timeSignature: string;
  filtersSignature: string;
}

export default function OptimizationTable({
  tableQueryUrl,
  countQueryUrl,
  onPageChange,
  onSortChange,
  dataLimit,
  currentSortField,
  currentOffset,
  timeSignature,
  filtersSignature,
}: OptimizationTableProps) {
  const api = useXFetch();

  const { data, isFetching, isPlaceholderData, isLoading } = useQuery<OptimizationTableQueryResult>(
    {
      queryKey: [
        'getOptimizationActions',
        tableQueryUrl,
        countQueryUrl,
        currentOffset,
        timeSignature,
        filtersSignature,
      ],
      refetchOnWindowFocus: false,
      enabled: !!tableQueryUrl && !!countQueryUrl,
      refetchInterval: TABLE_LIVE_REFRESH_MS,
      queryFn: async () => {
        const actionsResponse = await api<OptLogVolumeActionsResponse>(tableQueryUrl);
        const countResponse = await api<OptLogVolumeActionsCountResponse>(countQueryUrl);
        const actions = actionsResponse.actions ?? [];
        const count = countResponse.count ?? 0;
        return { actions, count };
      },
    }
  );

  const operationColumnDefs = useMemo(
    () => getOperationColumnDefs({ sortField: currentSortField }),
    [currentSortField]
  );
  const showLoader = (isLoading && !data) || (isFetching && !data && !isPlaceholderData);

  const rowData = useMemo(() => {
    if (
      showLoader &&
      (!(data as unknown as OptimizationTableQueryResult)?.actions ||
        (data as unknown as OptimizationTableQueryResult).actions.length === 0)
    ) {
      return generateSkeletonRows(dataLimit, createSkeletonAction()) as (OptLogVolumeActions & {
        _isSkeleton?: boolean;
      })[];
    }
    return (data as unknown as OptimizationTableQueryResult)?.actions || [];
  }, [(data as unknown as OptimizationTableQueryResult)?.actions, showLoader, dataLimit]);

  const lastKnownCountRef = useRef<number | undefined>(undefined);
  if ((data as unknown as OptimizationTableQueryResult)?.count !== undefined) {
    lastKnownCountRef.current = (data as unknown as OptimizationTableQueryResult).count;
  }
  const stableCount = lastKnownCountRef.current ?? 0;

  const currentPage = useMemo(
    () => Math.floor((parseInt(currentOffset, 10) || 0) / dataLimit),
    [currentOffset, dataLimit]
  );

  return (
    <div className="flex flex-col gap-[14px] mt-4">
      <div>
        <OptimizationActions
          totalActionsCount={stableCount}
          totalActionsCountIsLoading={isLoading}
          growActions={0}
          shrinkActions={0}
        />
      </div>

      <div className="flex flex-col pb-[14px] h-[520px] 2xl:h-[702px] ">
        <Table
          rowData={rowData}
          columnDefs={operationColumnDefs}
          defaultColDef={{ filter: false }}
          headerHeight={48}
          onSortChange={(sort) => {
            // Prevent sorting when loading skeleton data
            if (
              showLoader &&
              (!(data as unknown as OptimizationTableQueryResult)?.actions ||
                (data as unknown as OptimizationTableQueryResult).actions.length === 0)
            ) {
              return;
            }
            onSortChange(sort);
          }}
          itemsName={'actions'}
          noResultsText="No Results"
          pagination={{
            dataCount: showLoader ? 0 : stableCount,
            perPage: dataLimit,
            onChange: onPageChange,
            totalPages: Math.ceil(stableCount / dataLimit) || 1,
            currentPage,
          }}
          loading={false}
          suppressNoRowsWhileLoading={true}
          getRowId={(params) => {
            if (params.data._isSkeleton) {
              return params.data.volumeId;
            }
            return `${params.data.volumeId}-${params.data.actionFinishTime}`;
          }}
        />
      </div>
    </div>
  );
}
